import { AdminTicketService } from './../../services/admin-ticket.service'
import { ClientTicketService } from './../../services/client-ticket.service'
import { getApiErrorMessage } from './../../services/axios.service'
import { Creators as SiteCreators } from '../site/site'
import { toastActions } from '../../actions'
import { createActions, createReducer } from 'reduxsauce'

const initialState = {
	order: null,
	tickets: [],
	isLoading: false,
	isModalOpen: false,
	fetchTimestamp: '',
	pixData: {
		pixImageQrCode: '',
		pixValidity: null,
		pixEmv: '',
		provider: '',
	},
	isQRCodeModalOpen: false,
	promotionCouponCode: Object,
}

// FR-004: nenhum estado intermediário pode mostrar o pedido anterior. Antes desta limpeza, só
// `setData` escrevia `order`/`tickets`, e nada os apagava ao iniciar uma nova busca — se a busca
// do pedido B falhasse (ou ainda estivesse em voo), o que sobrava na tela era o pedido A.
const setFetchingData = (state = initialState, { fetchTimestamp }) => ({
	...state,
	fetchTimestamp,
	order: null,
	tickets: [],
})

const setData = (
	state = initialState,
	{ data, orderNumber, fetchTimestamp }
) => {
	const isSameRequest = state.fetchTimestamp == fetchTimestamp
	return {
		...state,
		orderNumber,
		isLoading: !isSameRequest,
		tickets: isSameRequest ? data.tickets : state.tickets,
		order: isSameRequest ? data.order : state.order,
		promotionCoupon: isSameRequest
			? data.promotionCouponCode
			: state.promotionCouponCode,
	}
}

// A MuviHub "me/orders/{id}" quase espelha o shape legado do ticket, com duas
// exceções de nome de campo (calculatedPrice/showRebooking) — normalizamos aqui
// para reaproveitar o mesmo pipeline de renderização (ModalTrip/TicketCard).
function normalizeOrderTickets(tickets) {
	return (tickets || []).map((ticket) => ({
		...ticket,
		calculedPrice: ticket.calculatedPrice,
		showReeboking: ticket.showRebooking,
	}))
}

// context pode vir como número (orderNumber puro, chamadas mais simples) ou como objeto
// { orderNumber, status, dateCreated, paymentType } (listagem "Minhas Compras" - ver
// ClientTicketService.getOrder pro porquê do fallback legado-only precisar dos três extras).
const fetchData = (orderId, context) => (dispatch) => {
	const timestamp = +new Date()
	dispatch(Creators.setFetchingData(timestamp))
	dispatch(SiteCreators.showLoading())
	var orderNumber = (context && context.orderNumber) || context
	return ClientTicketService.getOrder(orderId, typeof context === 'object' ? context : { orderNumber: context })
		.then((order) => {
			const data = {
				order,
				tickets: normalizeOrderTickets(order.tickets),
				promotionCouponCode: order.promotionCouponCode,
			}
			dispatch(Creators.setData(data, orderNumber, timestamp))
		})
		.catch((error) => {
			// FR-003: erro de rede ou 500/502 sem corpo não trazem `message` — o interceptor
			// central (axios.service.js/showMuviHubErrorToast) só mostra toast quando a API
			// devolve um. Sem este fallback o clique falha em silêncio total. Quando a API já
			// mandou uma mensagem própria, o interceptor já mostrou o toast: não duplicar aqui.
			if (!getApiErrorMessage(error)) {
				dispatch(
					toastActions.danger({
						message: 'Não foi possível abrir os detalhes deste pedido.',
					})
				)
			}
			// Repropaga: quem chamou (a linha da lista) precisa saber que falhou pra não abrir
			// o modal sem dado (ou com o dado do pedido anterior).
			throw error
		})
		.finally(() => dispatch(SiteCreators.hideLoading()))
}

const fetchDataAsAdmin = (orderNumber) => (dispatch) => {
	const timestamp = +new Date()
	dispatch(Creators.setFetchingData(timestamp))
	dispatch(SiteCreators.showLoading())
	return AdminTicketService.getTickets(orderNumber)
		.then((data) => {
			dispatch(Creators.setData(data, orderNumber, timestamp))
		})
		.finally(() => dispatch(SiteCreators.hideLoading()))
}

const removeTicket = (state = initialState, { ticketId }) => ({
	...state,
	tickets: state.tickets.map((item) => {
		if (
			item.ticketId == ticketId
		) {
			item.canReturn = false
			// Bilhete cancelado não pode mais ser remarcado.
			item.showReeboking = false
		}
		return item
	}),
})

const removeAllTickets = (
	state = initialState,
	{ ticketId, isGoing }
) => {
	const ticket = state.tickets.find(
		(o) => o.ticketId == ticketId
	)
	return {
		...state,
		tickets: state.tickets.map((item) => {
			if (item.departure == ticket.departure) {
				item.canReturn = false
				// Bilhete cancelado não pode mais ser remarcado.
				item.showReeboking = false
			}
			return item
		}),
	}
}

// Um bilhete "cancelAll" cancela todos os bilhetes do mesmo trecho (mesma
// data/horário de partida) que o bilhete informado, espelhando o agrupamento
// já usado pra exibição em ModalTrip.
function resolveTicketIdsToCancel(tickets, ticketId, cancelAll) {
	if (!cancelAll) return [ticketId]

	const target = (tickets || []).find((ticket) => ticket.ticketId === ticketId)

	if (!target) return [ticketId]

	return tickets
		.filter((ticket) => ticket.departure === target.departure)
		.map((ticket) => ticket.ticketId)
}

const returnTicket = (ticketId, cancelAll, isGoing) => (
	dispatch,
	getState
) => {
	dispatch(SiteCreators.showLoading())

	const { order, tickets } = getState().clientTicketModal

	// Pedidos da MuviHub (order presente) cancelam via me/orders/{id}/tickets/cancel;
	// o fluxo antigo (relatório do funcionário) continua em cliente/bilhete/devolver.
	const request = order
		? ClientTicketService.cancelOrderTickets(
				order.id,
				resolveTicketIdsToCancel(tickets, ticketId, cancelAll)
		  ).then((data) => ({ data, success: true, errors: null }))
		: ClientTicketService.returnTicket({ ticketId, cancelAll, isGoing })

	return request.then(({ data, success, errors }) => {
		dispatch(SiteCreators.hideLoading())
		if (success) {
			if (cancelAll) {
				dispatch(
					Creators.removeAllTickets(ticketId, isGoing)
				)
			} else {
				dispatch(Creators.removeTicket(ticketId))
			}
		}
		return { data, success, errors }
	})
}

const returnTicketAsAdmin = (ticketId, cancelAll, isGoing) => (
	dispatch
) => {
	dispatch(SiteCreators.showLoading())
	return AdminTicketService.returnTicket(
		ticketId,
		cancelAll,
		isGoing,
	).then(({ data, success, errors }) => {
		dispatch(SiteCreators.hideLoading())
		if (success) {
			if (cancelAll) {
				dispatch(
					Creators.removeAllTickets(ticketId, isGoing)
				)
			} else {
				dispatch(Creators.removeTicket(ticketId))
			}
		}

		return { data, success, errors }
	})
}

const openQrCodeModal = (state = initialState, { pixData }) => ({
	...state,
	isQRCodeModalOpen: true,
	pixData: pixData,
})

export const { Types, Creators } = createActions(
	{
		fetchData,
		returnTicket,
		openModal: null,
		closeModal: null,
		fetchDataAsAdmin,
		returnTicketAsAdmin,
		openQrCodeModal: ['pixData'],
		removeAllTickets: ['ticketId', 'isGoing'],
		setData: ['data', 'orderNumber', 'fetchTimestamp'],
		setFetchingData: ['fetchTimestamp'],
		removeTicket: ['ticketId'],
	},
	{
		prefix: 'client-ticket-modal/',
	}
)

const openModal = (state = initialState) => ({ ...state, isModalOpen: true })
const closeModal = (state = initialState) => ({
	...state,
	isModalOpen: false,
	isQRCodeModalOpen: false,
})

export default createReducer(initialState, {
	[Types.SET_DATA]: setData,
	[Types.OPEN_MODAL]: openModal,
	[Types.CLOSE_MODAL]: closeModal,
	[Types.REMOVE_TICKET]: removeTicket,
	[Types.SET_FETCHING_DATA]: setFetchingData,
	[Types.OPEN_QR_CODE_MODAL]: openQrCodeModal,
	[Types.REMOVE_ALL_TICKETS]: removeAllTickets,
})
